// ---------------------------------------------------------
// Datei: store/documentState.ts
// Zweck: Zentraler Speicher für den Zustand der Dokumente über die Navigation hinweg
// Besonderheiten:
// - Persistenter Zustand für Dokumente zwischen Seitenaufrufen
// - Speichert Upload-Status und Fortschritt
// - Ermöglicht optimistic Updates
// ---------------------------------------------------------

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getDocumentTypes } from "../services/api";
import { deleteDocument as apiDeleteDocument } from "../services/api.extensions";
import { DocumentType } from "../types/documents";
import {
  resolveSubstitutes,
  calculateProgressWithSubstitutes,
} from "../services/substituteResolver";

// Speichert den Zustand der Dokumente nach Phase
interface DocumentState {
  // Dokumenten-Zustand pro Phase
  documents: Record<string, DocumentType[]>;
  // Progress pro Phase
  progress: Record<string, { completed: number; required: number }>;
  // Phase-Status (für Häkchen-Persistenz)
  phases: Record<
    string,
    { status: "pending" | "completed"; completedAt?: string; completedByEmail?: string }
  >;
  // Loading-Status pro Phase
  loading: Record<string, boolean>;
  // Error pro Phase
  error: Record<string, string | null>;
  // Dokument-Daten für Vorschau (Base64-Strings für Bilder/PDFs)
  documentData: Record<string, string>; // Key: `${phaseId}-${docId}`

  // Aktionen
  fetchDocuments: (phaseId: string) => Promise<void>;
  setHubSpotDocumentStatus: (
    phaseId: string,
    documents: any[],
    progress: { required: number; completed: number }
  ) => void;
  updateDocumentStatus: (
    phaseId: string,
    docId: string,
    status: "completed" | "pending" | "substituted"
  ) => void;
  updatePhaseStatus: (
    phaseId: string,
    status: "pending" | "completed",
    completedAt?: string,
    completedByEmail?: string
  ) => void;
  getPhaseDocuments: (phaseId: string) => DocumentType[];
  getPhaseProgress: (phaseId: string) => {
    completed: number;
    required: number;
  };
  getPhaseStatus: (
    phaseId: string
  ) => { status: "pending" | "completed"; completedAt?: string; completedByEmail?: string } | null;
  isPhaseLoading: (phaseId: string) => boolean;
  getPhaseError: (phaseId: string) => string | null;
  // Dokument-Daten speichern/laden
  saveDocumentData: (phaseId: string, docId: string, data: string) => void;
  getDocumentData: (phaseId: string, docId: string) => string | null;
  // FileId für Dokument speichern (nach HubSpot-Upload)
  saveDocumentFileId: (phaseId: string, docId: string, fileId: string) => void;
  getDocumentFileId: (phaseId: string, docId: string) => string | null;
  // Dokument löschen (Backend + Store)
  deleteDocument: (phaseId: string, docId: string) => Promise<void>;
}

// Store mit Persistenz
export const useDocumentStore = create<DocumentState>()(
  persist(
    (set, get) => ({
      documents: {},
      progress: {},
      phases: {},
      loading: {},
      error: {},
      documentData: {},

      // Dokument löschen
      deleteDocument: async (phaseId: string, docId: string) => {
        try {
          // 1. Backend-Call (HubSpot Property leeren)
          await apiDeleteDocument(phaseId, docId);

          // 2. Store aktualisieren (Status auf pending, fileIds leeren)
          const currentDocs = get().documents[phaseId] || [];
          const updatedDocs = currentDocs.map((doc) => {
            if (doc._id === docId) {
              return {
                ...doc,
                status: "pending" as const,
                fileId: undefined,
                fileIds: [],
              };
            }
            return doc;
          });

          // Progress neu berechnen
          const resolvedDocs = resolveSubstitutes(updatedDocs);
          const { required: requiredCount, completed: completedCount } =
            calculateProgressWithSubstitutes(resolvedDocs);

          const dataKey = `${phaseId}-${docId}`;
          set((state) => {
            const { [dataKey]: _removed, ...remainingData } = state.documentData;
            return {
              documents: { ...state.documents, [phaseId]: updatedDocs },
              progress: {
                ...state.progress,
                [phaseId]: { required: requiredCount, completed: completedCount },
              },
              documentData: remainingData,
            };
          });

          console.log("[DOCUMENT-DELETED]", { phaseId, docId });
        } catch (error) {
          console.error("[DOCUMENT-DELETE-ERROR]", error);
          // Optional: Error-State setzen
        }
      },

      // Dokumente für eine Phase laden
      fetchDocuments: async (phaseId: string) => {
        if (!phaseId) return;

        // Loading-Status setzen
        set((state) => ({
          loading: { ...state.loading, [phaseId]: true },
        }));

        try {
          // Wenn Dokumente bereits geladen sind, nicht erneut laden
          const existingDocs = get().documents[phaseId];
          if (existingDocs && existingDocs.length > 0) {
            set((state) => ({
              loading: { ...state.loading, [phaseId]: false },
            }));
            return;
          }

          // Dokumente vom API laden
          const documentTypes = await getDocumentTypes(phaseId);

          if (Array.isArray(documentTypes)) {
            // Dokumente mit Status-Info ergänzen (für MVP)
            // In Produktion würden hier echte Status-Daten vom Backend kommen
            const docsWithStatus = documentTypes.map((doc) => ({
              ...doc,
              status: "pending" as const,
            }));

            // Ersatzdokumente auflösen und Status aktualisieren
            const resolvedDocs = resolveSubstitutes(docsWithStatus);

            // Progress mit Ersatzdokumenten berechnen
            const { required: requiredCount, completed: completedCount } =
              calculateProgressWithSubstitutes(resolvedDocs);

            // ✅ B-01: Strategic Logging (B-22 Learning)
            console.log("[STORE-SYNC]", {
              phaseId,
              docsCount: docsWithStatus.length,
              required: requiredCount,
              completed: completedCount,
            });

            // State aktualisieren
            set((state) => ({
              documents: { ...state.documents, [phaseId]: docsWithStatus },
              progress: {
                ...state.progress,
                [phaseId]: {
                  required: requiredCount,
                  completed: completedCount,
                },
              },
              loading: { ...state.loading, [phaseId]: false },
              error: { ...state.error, [phaseId]: null },
            }));
          }
        } catch (err: any) {
          const errorMessage =
            err.message || "Fehler beim Laden der Dokumenttypen";

          set((state) => ({
            loading: { ...state.loading, [phaseId]: false },
            error: { ...state.error, [phaseId]: errorMessage },
          }));
        }
      },

      // HubSpot-Dokument-Status setzen (für HubSpot-Deals)
      setHubSpotDocumentStatus: (
        phaseId: string,
        documents: any[],
        progress: { required: number; completed: number }
      ) => {
        // State aktualisieren mit vorbereiteten Dokumenten
        // Dokumente die bereits lokal via Drive hochgeladen wurden behalten ihren Status
        set((state) => {
          const mergedDocs = documents.map((doc) => {
            const driveUrl = state.documentData?.[`${phaseId}-${doc._id}`];
            if (driveUrl?.startsWith('https://drive.google.com/')) {
              return { ...doc, status: 'completed' };
            }
            return doc;
          });
          const completedCount = mergedDocs.filter((d) => d.status === 'completed' && d.required).length;
          const mergedProgress = { ...progress, completed: Math.max(progress.completed, completedCount) };
          return {
            documents: { ...state.documents, [phaseId]: mergedDocs },
            progress: { ...state.progress, [phaseId]: mergedProgress },
            loading: { ...state.loading, [phaseId]: false },
          };
        });

        console.log("[HUBSPOT-DOCS-LOADED]", {
          phaseId,
          docsCount: documents.length,
          progress,
        });
      },

      // Status eines Dokuments aktualisieren (optimistic update)
      updateDocumentStatus: (
        phaseId: string,
        docId: string,
        status: "completed" | "pending" | "substituted"
      ) => {
        const currentDocs = get().documents[phaseId] || [];

        // Dokument finden und Status aktualisieren
        const updatedDocs = currentDocs.map((doc) =>
          doc._id === docId ? { ...doc, status } : doc
        );

        // Ersatzdokumente auflösen
        const resolvedDocs = resolveSubstitutes(updatedDocs);

        // Progress mit Ersatzdokumenten berechnen
        const { required: requiredCount, completed: completedCount } =
          calculateProgressWithSubstitutes(resolvedDocs);

        // ✅ B-01: Strategic Logging (B-22 Learning)
        console.log("[PROGRESS-UPDATE]", {
          phaseId,
          docId,
          status,
          required: requiredCount,
          completed: completedCount,
        });

        // State aktualisieren
        set((state) => ({
          documents: { ...state.documents, [phaseId]: updatedDocs },
          progress: {
            ...state.progress,
            [phaseId]: { required: requiredCount, completed: completedCount },
          },
        }));
      },

      // Dokumente für eine Phase abrufen
      getPhaseDocuments: (phaseId: string) => {
        return get().documents[phaseId] || [];
      },

      // Progress für eine Phase abrufen
      getPhaseProgress: (phaseId: string) => {
        return get().progress[phaseId] || { completed: 0, required: 0 };
      },

      // Phase-Status aktualisieren (für B-02 Completion)
      updatePhaseStatus: (
        phaseId: string,
        status: "pending" | "completed",
        completedAt?: string,
        completedByEmail?: string
      ) => {
        console.log("[PHASE-STATUS-UPDATE]", { phaseId, status, completedAt, completedByEmail });

        set((state) => ({
          phases: {
            ...state.phases,
            [phaseId]: { status, completedAt, completedByEmail },
          },
        }));
      },

      // Phase-Status abrufen
      getPhaseStatus: (phaseId: string) => {
        return get().phases[phaseId] || null;
      },

      // Prüfen, ob Dokumente für eine Phase geladen werden
      isPhaseLoading: (phaseId: string) => {
        return get().loading[phaseId] || false;
      },

      // Fehler für eine Phase abrufen
      getPhaseError: (phaseId: string) => {
        return get().error[phaseId] || null;
      },

      // Dokument-Daten speichern (für Vorschau)
      saveDocumentData: (phaseId: string, docId: string, data: string) => {
        const key = `${phaseId}-${docId}`;
        set((state) => ({
          documentData: { ...state.documentData, [key]: data },
        }));
        console.log("[DOCUMENT-DATA-SAVED]", {
          phaseId,
          docId,
          dataLength: data.length,
        });
      },

      // Dokument-Daten abrufen (für Vorschau)
      getDocumentData: (phaseId: string, docId: string) => {
        const key = `${phaseId}-${docId}`;
        return get().documentData[key] || null;
      },

      // FileId für Dokument speichern (nach HubSpot-Upload)
      saveDocumentFileId: (phaseId: string, docId: string, fileId: string) => {
        const currentDocs = get().documents[phaseId] || [];

        // Dokument finden und fileId hinzufügen
        const updatedDocs = currentDocs.map((doc) => {
          if (doc._id === docId) {
            // Multi-Upload Logic: Append ID
            const existingIds = doc.fileIds || [];
            // Avoid duplicates just in case
            const newFileIds = existingIds.includes(fileId)
              ? existingIds
              : [...existingIds, fileId];

            return {
              ...doc,
              fileId, // Keep latest for backward compat
              fileIds: newFileIds,
              status: "completed" as const,
            };
          }
          return doc;
        });

        // State aktualisieren
        set((state) => ({
          documents: { ...state.documents, [phaseId]: updatedDocs },
        }));

        console.log("[DOCUMENT-FILEID-SAVED]", { phaseId, docId, fileId });
      },

      // FileId für Dokument abrufen
      getDocumentFileId: (phaseId: string, docId: string) => {
        const docs = get().documents[phaseId] || [];
        const doc = docs.find((d) => d._id === docId);
        return (doc as any)?.fileId || null;
      },
    }),
    {
      name: "360volt-document-store",
      storage: createJSONStorage(() => {
        if (
          typeof window !== "undefined" &&
          window.localStorage &&
          typeof window.localStorage.getItem === "function" &&
          typeof window.localStorage.setItem === "function" &&
          typeof window.localStorage.removeItem === "function"
        ) {
          return window.localStorage;
        }

        const memoryStorage = new Map<string, string>();
        return {
          getItem: (key: string) => memoryStorage.get(key) ?? null,
          setItem: (key: string, value: string) => {
            memoryStorage.set(key, value);
          },
          removeItem: (key: string) => {
            memoryStorage.delete(key);
          },
        };
      }),
      partialize: (state) => ({
        documents: state.documents,
        progress: state.progress,
        phases: state.phases,
        documentData: state.documentData,
      }),
    }
  )
);
